/** Console image-gallery API — `/{activities|itineraries}/<id>/images/`.
 *
 * Hand-authored (console convention). One parameterized module serves both
 * families; `makeGalleryApi(kind, parentId)` builds the `ImageGalleryApi` the
 * `<ImageGallery>` component consumes. For itineraries, `parentId` is the
 * Itinerary_Group id (the backend resolves its default itinerary). Uploads are
 * batch multipart; a JSON PATCH edits metadata; a multipart PATCH with `file`
 * replaces the image in place.
 */
import type { ImageChunks } from '@/utils/imageUrl';

import { apiDelete, apiGet, apiPatch, apiPost, client } from './client';

export interface GalleryImage {
  id: number;
  image: ImageChunks | null;
  caption: string | null;
  video_url: string | null;
  sequence: number | null;
  is_360_degree: boolean;
  offset_360_degree: number;
  /** Itinerary images only; null for activity images. */
  show_in_group: boolean | null;
  download_status: number;
  source_url: string | null;
}

export interface GalleryPatch {
  caption?: string | null;
  video_url?: string | null;
  is_360_degree?: boolean;
  offset_360_degree?: number;
  show_in_group?: boolean;
}

export type GalleryKind = 'activities' | 'itineraries';

const base = (kind: GalleryKind, parentId: number): string => `/${kind}/${parentId}/images/`;

export async function listImages(kind: GalleryKind, parentId: number): Promise<GalleryImage[]> {
  return (await apiGet<{ results: GalleryImage[] }>(base(kind, parentId))).results;
}

export async function uploadImages(kind: GalleryKind, parentId: number, files: File[]): Promise<GalleryImage[]> {
  const fd = new FormData();
  files.forEach((f, i) => fd.append(`file${i}`, f));
  const r = await client.post<{ results: GalleryImage[] }>(base(kind, parentId), fd, {
    headers: { 'Content-Type': undefined },
  });
  return r.data.results;
}

export function updateImage(
  kind: GalleryKind,
  parentId: number,
  imageId: number,
  patch: GalleryPatch,
): Promise<GalleryImage> {
  return apiPatch<GalleryImage>(`${base(kind, parentId)}${imageId}/`, patch);
}

export async function replaceImage(
  kind: GalleryKind,
  parentId: number,
  imageId: number,
  file: File,
): Promise<GalleryImage> {
  const fd = new FormData();
  fd.append('file', file);
  const r = await client.patch<GalleryImage>(`${base(kind, parentId)}${imageId}/`, fd, {
    headers: { 'Content-Type': undefined },
  });
  return r.data;
}

export function deleteImage(kind: GalleryKind, parentId: number, imageId: number): Promise<void> {
  return apiDelete(`${base(kind, parentId)}${imageId}/`);
}

export async function reorderImages(kind: GalleryKind, parentId: number, ids: number[]): Promise<GalleryImage[]> {
  return (await apiPost<{ results: GalleryImage[] }>(`${base(kind, parentId)}reorder/`, { ids })).results;
}

/** The parent-scoped api the `<ImageGallery>` component consumes. */
export interface ImageGalleryApi {
  list(): Promise<GalleryImage[]>;
  upload(files: File[]): Promise<GalleryImage[]>;
  update(id: number, patch: GalleryPatch): Promise<GalleryImage>;
  replace(id: number, file: File): Promise<GalleryImage>;
  remove(id: number): Promise<void>;
  reorder(ids: number[]): Promise<GalleryImage[]>;
}

export function makeGalleryApi(kind: GalleryKind, parentId: number): ImageGalleryApi {
  return {
    list: () => listImages(kind, parentId),
    upload: (files) => uploadImages(kind, parentId, files),
    update: (id, patch) => updateImage(kind, parentId, id, patch),
    replace: (id, file) => replaceImage(kind, parentId, id, file),
    remove: (id) => deleteImage(kind, parentId, id),
    reorder: (ids) => reorderImages(kind, parentId, ids),
  };
}
